<script setup lang="ts">
import {
	computed, ref, watch, 
} from "vue";

import { formatDistance } from "@ca/formatters/distance";
import { formatSecondsToHoursAndMinutes } from "@ca/formatters/durations";
import { formatInt } from "@ca/formatters/numbers";
import { formatPercent } from "@ca/formatters/percent";

import { useAircraftClass } from "@/composables/aircraft";
import { useAircraftStats, useUpdateAircraftConfig } from "@/queries/aircraft";
const settingsOpen = ref(false);
const props = defineProps<{
	aircraftId: string;
}>();

const refLandingThreshold = ref(4000);

const { mutate: updateAircraftConfig } = useUpdateAircraftConfig(props.aircraftId, "landing_threshold", refLandingThreshold);


const { data } = useAircraftStats(() => props.aircraftId);
const aircraftClass = useAircraftClass();

watch(() => data.value, () => {
	refLandingThreshold.value = data.value?.shortStripLandings.landingThreshold ?? 4000;
});

const saveLandingThreshold = async () => {
	await updateAircraftConfig();
	settingsOpen.value = false;
};

const stats = computed(() => {
	const stats = data.value ?? {
		avgDuration: 0,
		flightCount: 0,
		totalDuration: 0,
		utilization: 0,
		sinkRates: {
			flightsWithSinkRateEvents: 0,
			totalFlights: 0,
			percentage: 0,
		},
		shortStripLandings: {
			landingThreshold: 0,
			flightCount: 0,
		},
		intlFlightCount: 0,
		lateNightFlights: {
			count: 0,
			flights: [],
		},
		fatigue: {
			flightsWithFatigueEvents: 0,
			totalFlights: 0,
			percentage: 0,
		},
	};

	let sinkRateColor: "success" | "warning" | "error" | "neutral" = "success";
	if (stats.sinkRates.percentage >= 0.33) {
		sinkRateColor = "error";
	} else if (stats.sinkRates.percentage >= 0.15) {
		sinkRateColor = "warning";
	}
	let fatigueColor: "success" | "warning" | "error" | "neutral" = "success";
	if (stats.fatigue.percentage >= 0.10) {
		fatigueColor = "error";
	} else if (stats.fatigue.percentage >= 0.05) {
		fatigueColor = "warning";
	}
	const statList = [
		{
			label: "Flight Count",
			value: formatInt(stats.flightCount),
		},
		{
			label: "Flight Time",
			value: formatSecondsToHoursAndMinutes(stats.totalDuration),
			badgeValue: `Utilization ${formatPercent(stats.utilization)}`,
			badgeColor: "neutral",
			badgeVariant: "subtle",
		},
		{
			label: "Avg Duration",
			value: formatSecondsToHoursAndMinutes(stats.avgDuration),
		},
		{
			label: "International Flights",
			value: formatInt(stats.intlFlightCount),
		},
		{
			label: "Sink Rate Exceedence",
			value: `${stats.sinkRates.flightsWithSinkRateEvents} / ${stats.sinkRates.totalFlights}`,
			badgeValue: `${formatPercent(stats.sinkRates.percentage, 1)}`,
			badgeColor: sinkRateColor,
			hidden: aircraftClass.value === "Rotor-wing",
		},
		{
			label: "Short Strip Landings",
			value: `${stats.shortStripLandings.flightCount}`,
			badgeValue: `< ${formatDistance({ distance: stats.shortStripLandings.landingThreshold, unit: "foot" })}`,
			badgeColor: "neutral",
			showConfig: true,
			hidden: aircraftClass.value === "Rotor-wing",
		},
		{
			label: "Late Night Flights",
			value: `${stats.lateNightFlights.count}`,
		},
		{
			label: "Fatigue Events",
			value: `${stats.fatigue.flightsWithFatigueEvents} / ${stats.fatigue.totalFlights}`,
			badgeValue: `${formatPercent(stats.fatigue.percentage, 1)}`,
			badgeColor: fatigueColor,
		},
	] satisfies {
		label: string;
		value: string;
		badgeValue?: string;
		badgeColor?: "success" | "warning" | "error" | "neutral";
		badgeVariant?: "subtle" | "solid";
		showConfig?: boolean;
		hidden?: boolean;
	}[];

	return statList.filter((stat) => !stat.hidden);
});

</script>
    
<template>
  <div>
    <h3 class="font-cta text-brand-500 text-xl/none">
      Stats
    </h3>
    <div class="grid grid-cols-2 gap-4 mt-4">
      <UCard
        v-for="stat in stats"
        :key="stat.label"
        variant="subtle"
      >
        <template #header>
          <div class="flex items-center justify-between">
            <h4 class="font-cta text-brand-500 text-base/none">
              {{ stat.label }}
            </h4>
            <UModal
              v-if="stat.showConfig"
              v-model:open="settingsOpen"
              title="Aircraft Configuration"
            >
              <UButton
                icon="ic-round-settings"
                variant="ghost"
                color="neutral"
                size="xxs"
                @click="stat.showConfig = true"
              />
              <template #body>
                <UFormField
                  label="Landing Threshold (ft)"
                  name="landingThreshold"
                >
                  <UInput
                    v-model="refLandingThreshold"
                    type="number"
                  />
                </UFormField>
              </template>
              <template #footer>
                <div class="flex justify-end">
                  <UButton
                    label="Save"
                    @click="saveLandingThreshold"
                  />
                </div>
              </template>
            </UModal>
          </div>
        </template>
        <p class="text-2xl/none text-center">
          {{ stat.value }}
        </p>
        <div
          v-if="stat.badgeValue"
          class="flex justify-center mt-2"
        >
          <UBadge
            :color="stat.badgeColor"
            :variant="stat.badgeVariant"
          >
            {{ stat.badgeValue }}
          </UBadge>
        </div>
      </UCard>
    </div>
  </div>
</template>